// Identity Pill — the upper-right chrome that follows the viewer everywhere.
//
// Jul 2026 (Randy) — turned into the app's single nav dropdown, IDENTICAL on
// desktop and mobile. Tapping the pill/star opens a panel that:
//   • keeps the identity info (name · role · invited-by) at the TOP — tapping
//     that block goes to the viewer's home (creator → dashboard, fan → portal),
//   • then role-aware destinations, consolidating what used to be separate
//     top-bar buttons (Admin, Account & Billing, Sign out) so the top bar is
//     clean — a big win on mobile.
//   • Dark mode toggle sits at the BOTTOM, just above Log out (moved here from
//     the dashboard, Jul 2026).
//
// The panel is PORTALED to <body> and positioned `fixed` under the trigger so
// it can never be painted over by the dashboard's sticky tab bar (z-30) —
// which was clipping the menu's lower rows (Jul 2026 fix).
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Sparkle, ShieldCheck, House, Books, CreditCard, GearSix, SignOut, Heart, ChatCircleText, Moon, PaintBrush, CaretDown, ChartLine, Robot, X, CalendarHeart } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, clearAuth, getUser } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/useViewerSurface";
import { useDarkMode } from "@/lib/darkMode";
import { faceImageUrl } from "@/lib/siloArt";
import { SiloArtModeSlider } from "@/components/SiloArtLayer";
import { ReportModal } from "@/components/UsageOverview";
import LockGate from "@/components/LockGate";
import { useEntitlements } from "@/lib/entitlements";
import { useCreatorProGate } from "@/components/CreatorProGateProvider";

// Soft, paper-tone palette. Each role gets a barely-there accent.
const ROLE_ACCENT = {
  super_admin: { from: "#C16E5A", label: "Super Admin" },
  affiliate_creator: { from: "#1C1B1A", label: "Creator Pro" },
  creator: { from: "#4A5D4E", label: "Creator" },
  new_creator: { from: "#A89360", label: "New Creator" },
  fan: { from: "#9B9893", label: "Fan" },
};

// Jul–Oct 2026 (Randy) — Dark mode · Silo Art · Silo Widget are ONE designed
// group at the foot of the menu, so they share these metrics exactly: same
// height, padding, type, label alignment and spacing. Silo Art's slider is
// 54px wide (three stages) against the others' 36px two-state switch, so the
// control CLUSTER is right-aligned and the caret gets a reserved slot in every
// row — that keeps every toggle on one right edge and every row the same
// height whether it carries a disclosure arrow or not.
const CTRL_ROW = "w-full flex items-center justify-between gap-2.5 px-3.5 py-2.5 text-left text-[13px] font-normal text-[#1C1B1A] hover:bg-[#F0F1EE] transition-colors";
const CTRL_LABEL = "flex items-center gap-2.5 min-w-0";
const CTRL_SIDE = "flex items-center justify-end gap-2 shrink-0";
const CTRL_CARET = "w-3.5 flex items-center justify-center shrink-0";

const PillSwitch = ({ on }) => (
  <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${on ? "bg-[#4A5D4E]" : "bg-[#D8D5CD]"}`}>
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${on ? "translate-x-4" : "translate-x-0.5"}`} />
  </span>
);

export default function IdentityPill({ inline = true, siloId = null, siloSlug = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [subs, setSubs] = useState(null); // fan subscriptions, lazily loaded
  const [menuPos, setMenuPos] = useState(null);
  const { enabled: darkEnabled, toggle: toggleDark } = useDarkMode();
  const [artOpen, setArtOpen] = useState(false);
  // Jul 2026 (Randy) — Silo Library is a "smart dropdown": first tap expands to
  // reveal "Usage report", second tap opens the Library. usageOpen drives the
  // same Usage Report popup used on the silo's Operations tab.
  const [libExpanded, setLibExpanded] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);
  const [siloPicker, setSiloPicker] = useState(null); // { stage, silos } when picking a silo
  // P7 (Oct 2026) — Silo Anywhere on My Silo's own silo pages. Account-level
  // switch, ON-PLATFORM ONLY: external embeds are governed by each silo's own
  // Disable/Revoke and are deliberately untouched by this.
  const [saOn, setSaOn] = useState(null);
  const wrapperRef = useRef(null);
  const menuRef = useRef(null);
  const nav = useNavigate();
  // Gate the "AI Assistant" shortcut on the master_ai SKU (same gate the
  // full-page assistant + Workbench assistant use).
  const { locked: entLocked, lockStyle: entLockStyle } = useEntitlements();
  const gate = useCreatorProGate();

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/me/identity");
        if (!cancel) setData(r.data);
      } catch { /* unauthenticated */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  // Position the portaled dropdown just under the trigger. Reposition on
  // scroll/resize so it tracks the pill.
  useEffect(() => {
    if (!open) return;
    const place = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setMenuPos({
        top: Math.round(r.bottom + 8),
        right: Math.round(Math.max(8, window.innerWidth - r.right)),
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  // Tap anywhere outside (trigger OR portaled menu) → close.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapperRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("touchstart", onClick);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("touchstart", onClick);
    };
  }, [open]);

  // Fans: pull their subscribed Public Q&As the first time they open the menu.
  useEffect(() => {
    if (!open || !data || data.role !== "fan" || subs !== null) return;
    (async () => {
      try {
        const r = await api.get("/fan/portal");
        setSubs((r.data?.subscriptions || []).slice(0, 10));
      } catch { setSubs([]); }
    })();
  }, [open, data, subs]);

  // Phase A (Jul 2026, Randy) — Family Access Point. Personal silos this user
  // was invited to (not owned) get a "Family · <name>" row → /family-view/:id.
  const [famSilos, setFamSilos] = useState(null);
  useEffect(() => {
    if (!open || !data || famSilos !== null) return;
    (async () => {
      try {
        const me = getUser();
        const r = await api.get("/silos");
        setFamSilos((r.data || [])
          .filter((s) => s.silo_kind === "personal" && s.owner_user_id !== me?.id)
          .slice(0, 5));
      } catch { setFamSilos([]); }
    })();
  }, [open, data, famSilos]);

  // Collapse the Silo Library sub-menu whenever the whole panel closes, so the
  // next open starts collapsed (first tap expands, second tap opens Library).
  useEffect(() => { if (!open) setLibExpanded(false); }, [open]);

  if (loading || !data) return null;

  const accent = ROLE_ACCENT[data.role] || ROLE_ACCENT.fan;
  const roleLabel = data.role_label || ROLE_LABELS[data.role] || data.role;
  const isFan = data.role === "fan";
  const homePath = isFan ? "/fan-portal" : "/dashboard";

  const go = (path) => { setOpen(false); nav(path); };
  const goLibrary = () => {
    setOpen(false);
    try { sessionStorage.setItem(`silo_tab:${siloId}`, "library"); } catch { /* */ }
    nav(`/dashboard/silo/${siloId}`);
  };
  // Unified billing (fan + creator): open the Stripe-hosted portal for their
  // ONE canonical customer — every subscription they have, in one place.
  const openBilling = async () => {
    setOpen(false);
    try {
      const r = await api.post("/stripe/billing/portal", { origin_url: window.location.origin });
      if (r.data?.url) window.location.href = r.data.url;
      else toast.error("Could not open billing.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing.");
    }
  };
  const doLogout = () => { setOpen(false); clearAuth(); nav("/login"); };

  const saEnabled = saOn === null ? data.my_silo_anywhere_enabled !== false : saOn;
  const toggleSiloAnywhere = async (e) => {
    e.stopPropagation();
    const next = !saEnabled;
    setSaOn(next);
    try {
      await api.patch("/me/silo-anywhere", { enabled: next });
      try { window.dispatchEvent(new CustomEvent("mysilo:silo-anywhere-changed")); } catch { /* */ }
      toast.success(next
        ? "Silo Widget back on your silo pages."
        : "Silo Widget hidden on My Silo. Your website embed keeps running.");
    } catch {
      setSaOn(!next);
      toast.error("Could not save that.");
    }
  };

  // Silo Art shortcut → hand the studio a (siloId, stage) via sessionStorage,
  // then dispatch a window event that BOTH Dashboard AND SiloManage listen
  // for. Feb 2026 (Randy pt-siloart-quickaccess-nav-fix) — previously this
  // always `nav("/dashboard")` before firing the event, so opening Silo Art
  // from the identity pill while inside a silo (e.g. on the YTRE tab)
  // punted the user back to the dashboard. When we ARE already on a silo
  // route, stay put — SiloManage now mounts its own SiloImageStudio and
  // listens for the same event.
  const openStudioFor = (siloIdToOpen, stage) => {
    try { sessionStorage.setItem("mysilo_open_studio", JSON.stringify({ siloId: siloIdToOpen, stage })); } catch { /* */ }
    setOpen(false);
    setArtOpen(false);
    setSiloPicker(null);
    // Only navigate if we're NOT inside a silo (i.e. the identity pill was
    // opened from the dashboard/account/other route). Inside a silo, the
    // event alone drives the SiloManage-hosted studio mount.
    const insideSilo = typeof window !== "undefined" && /^\/dashboard\/silo\//.test(window.location.pathname);
    if (!insideSilo) {
      nav("/dashboard");
    }
    setTimeout(() => { try { window.dispatchEvent(new CustomEvent("mysilo:open-studio")); } catch { /* */ } }, 80);
  };
  const openArtStage = async (stage) => {
    if (siloId) { openStudioFor(siloId, stage); return; }
    // Not inside a silo → let them pick which silo's art to set up.
    setOpen(false);
    try {
      const r = await api.get("/silos");
      setSiloPicker({ stage, silos: r.data || [] });
    } catch { toast.error("Couldn't load your silos."); }
  };

  // Middle rows (between info block and Log out), role-aware.
  const rows = [];
  if (!isFan) {
    if (data.role === "super_admin") {
      rows.push({ icon: ShieldCheck, label: "Super Admin", onClick: () => go("/admin"), testid: "identity-menu-admin" });
    }
    rows.push({ icon: House, label: "Creator Dashboard", onClick: () => go("/dashboard"), testid: "identity-menu-dashboard" });
    if (siloId) rows.push({ custom: "silo-library" });
    rows.push({ icon: GearSix, label: "Account settings", onClick: () => go("/dashboard/account-settings"), testid: "identity-menu-account" });
  } else {
    rows.push({ icon: Heart, label: "Fan Portal", onClick: () => go("/fan-portal"), testid: "identity-menu-fan-portal" });
    // Feb 2026 (Randy pt89c) — fans need their own settings surface for
    // name / email / password / billing. AccountSettings is heavily
    // creator-oriented; FanSettings is the lean fan version.
    rows.push({ icon: GearSix, label: "Fan Settings", onClick: () => go("/fan/settings"), testid: "identity-menu-fan-settings" });
  }
  // Shared, identical Billing entry for everyone → the unified Stripe portal.
  rows.push({ icon: CreditCard, label: "Billing", onClick: openBilling, testid: "identity-menu-billing" });

  const Row = ({ icon: Icon, label, onClick, testid }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px] text-[#1C1B1A] hover:bg-[#F0F1EE] transition-colors"
    >
      <Icon size={16} weight="duotone" className="shrink-0 text-[#6B6A68]" />
      <span className="truncate">{label}</span>
    </button>
  );

  // Jul 2026 (Randy) — Silo Library "smart dropdown". First tap on the label
  // expands to reveal "Usage report"; tapping the label AGAIN opens the Library.
  // "Usage report" opens the exact same popup as the silo's Operations tab.
  const libraryRow = (
    <div key="silo-library" data-testid="identity-menu-silo-library-row">
      <div className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 text-[13px] text-[#1C1B1A] hover:bg-[#F0F1EE] transition-colors">
        <button
          type="button"
          onClick={() => { if (!libExpanded) setLibExpanded(true); else goLibrary(); }}
          data-testid="identity-menu-silo-library"
          aria-expanded={libExpanded}
          title={libExpanded ? "Open the Silo Library" : "Tap to expand · tap again to open the Library"}
          className="flex items-center gap-2.5 flex-1 text-left"
        >
          <Books size={16} weight="duotone" className="shrink-0 text-[#6B6A68]" />
          Silo Library
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setLibExpanded((v) => !v); }}
          data-testid="identity-menu-silo-library-caret"
          aria-label="Show Silo Library options"
        >
          <CaretDown size={13} className={`text-[#6B6A68] transition-transform ${libExpanded ? "rotate-180" : ""}`} />
        </button>
      </div>
      {libExpanded && (
        <div className="bg-[#FAFAF7]" data-testid="identity-menu-silo-library-sub">
          {siloSlug && (
            <LockGate
              locked={entLocked("master_ai")}
              lockStyle={entLockStyle}
              onLockedClick={() => { setOpen(false); gate?.openGate?.("master_ai"); }}
              className="block w-full"
            >
              <button
                type="button"
                onClick={() => { setOpen(false); nav(`/silo/${siloSlug}/assistant`); }}
                data-testid="identity-menu-ai-assistant"
                className="w-full flex items-center gap-2.5 pl-10 pr-3.5 py-2 text-left text-[12px] text-[#1C1B1A] hover:bg-[#F0F1EE] transition-colors"
              >
                <Robot size={14} weight="duotone" className="shrink-0 text-[#6B6A68]" />
                AI Assistant
              </button>
            </LockGate>
          )}
          <button
            type="button"
            onClick={() => { setOpen(false); setUsageOpen(true); }}
            data-testid="identity-menu-usage-report"
            className="w-full flex items-center gap-2.5 pl-10 pr-3.5 py-2 text-left text-[12px] text-[#1C1B1A] hover:bg-[#F0F1EE] transition-colors"
          >
            <ChartLine size={14} weight="duotone" className="shrink-0 text-[#6B6A68]" />
            Usage report
          </button>
        </div>
      )}
    </div>
  );

  const panel = (
    <div
      ref={menuRef}
      data-testid="identity-menu"
      style={{ position: "fixed", top: menuPos?.top ?? 0, right: menuPos?.right ?? 8 }}
      className="w-[252px] bg-white border border-[#D8D6D2] rounded-xl shadow-2xl overflow-hidden z-[9999]"
    >
      {/* Identity info block — tapping it goes home. Kept intact. */}
      <button
        type="button"
        onClick={() => go(homePath)}
        data-testid="identity-menu-info"
        style={{ background: `linear-gradient(90deg, ${accent.from}12 0%, transparent 60%)` }}
        className="w-full text-left px-3.5 py-3 hover:bg-[#F7F6F2] transition-colors"
      >
        <div className="flex items-center gap-1.5 text-[13px] font-medium leading-tight text-[#1C1B1A]">
          <span data-testid="identity-pill-name" className="truncate max-w-[150px]">{data.identity_label}</span>
          <span className="opacity-40">/</span>
          <span data-testid="identity-pill-role" className="uppercase tracking-[0.06em] text-[10px]">{roleLabel}</span>
        </div>
        <div data-testid="identity-pill-invited-by" className="text-[10px] text-[#6B6A68] leading-tight mt-1">
          {data.invited_by_label
            ? <>Invited by <strong className="font-semibold text-[#1C1B1A]" data-testid="identity-pill-invited-by-name">{data.invited_by_label}</strong></>
            : <>Invited by —</>}
        </div>
      </button>

      <div className="border-t border-[#EDEBE6]" />
      {rows.map((r) => (r.custom === "silo-library" ? libraryRow : <Row key={r.testid} {...r} />))}

      {/* Family silos (Phase A) — the access point into the shared organizer
          for invited household members (any role). */}
      {famSilos && famSilos.map((s) => (
        <Row
          key={`fam-${s.id}`}
          icon={CalendarHeart}
          label={`Family · ${s.name}`}
          onClick={() => go(`/family-view/${s.id}`)}
          testid={`identity-menu-family-${s.slug}`}
        />
      ))}

      {/* Fans: their subscribed Public Q&As (up to 10). */}
      {isFan && subs === null && (
        <div className="px-3.5 py-2.5 text-[12px] text-[#6B6A68]" data-testid="identity-menu-qa-loading">Loading your Q&amp;As…</div>
      )}
      {isFan && subs && subs.length === 0 && (
        <div className="px-3.5 py-2.5 text-[12px] text-[#6B6A68]" data-testid="identity-menu-qa-empty">No subscriptions yet.</div>
      )}
      {isFan && subs && subs.map((s, i) => (
        <Row
          key={s.silo_slug || i}
          icon={ChatCircleText}
          label={s.silo_name || s.silo_slug}
          onClick={() => go(`/s/${s.silo_slug}`)}
          testid={`identity-menu-qa-${i}`}
        />
      ))}

      <div className="border-t border-[#EDEBE6]" />
      {/* Dark mode — Jul 2026 (Randy): moved here from the dashboard. Toggling
          keeps the menu open so the change is visible. */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); toggleDark(); }}
        role="switch"
        aria-checked={darkEnabled}
        data-testid="identity-menu-dark-mode"
        className={CTRL_ROW}
      >
        <span className={CTRL_LABEL}>
          <Moon size={16} weight="duotone" className="shrink-0 text-[#6B6A68]" />
          Dark mode
        </span>
        <span className={CTRL_SIDE}>
          <PillSwitch on={darkEnabled} />
          <span className={CTRL_CARET} aria-hidden="true" />
        </span>
      </button>

      {/* Silo Art — Randy (Jul 2026): a backup way into the cube art studio,
          right under Dark mode. Expands to Stage 1 / Stage 2 and jumps straight
          to the studio for the silo you're in (or asks which one). */}
      {!isFan && (
        <>
          <div className={CTRL_ROW} data-testid="identity-menu-silo-art-row">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setArtOpen((v) => !v); }}
              aria-expanded={artOpen}
              data-testid="identity-menu-silo-art"
              className={`${CTRL_LABEL} flex-1 text-left`}
            >
              <PaintBrush size={16} weight="duotone" className="shrink-0 text-[#6B6A68]" />
              Silo Art
            </button>
            <span className={CTRL_SIDE}>
              <SiloArtModeSlider compact />
              <button type="button" onClick={(e) => { e.stopPropagation(); setArtOpen((v) => !v); }}
                data-testid="identity-menu-silo-art-caret" aria-label="Show Silo Art stages"
                className={CTRL_CARET}>
                <CaretDown size={13} className={`text-[#6B6A68] transition-transform ${artOpen ? "rotate-180" : ""}`} />
              </button>
            </span>
          </div>
          {artOpen && (
            <div className="bg-[#FAFAF7]" data-testid="identity-menu-silo-art-sub">
              <button type="button" onClick={() => openArtStage("stage1")} data-testid="identity-menu-silo-art-stage1"
                className="w-full flex items-center gap-2.5 pl-10 pr-3.5 py-2 text-left text-[12px] text-[#1C1B1A] hover:bg-[#F0F1EE] transition-colors">
                Stage 1 · Create images
              </button>
              <button type="button" onClick={() => openArtStage("stage2")} data-testid="identity-menu-silo-art-stage2"
                className="w-full flex items-center gap-2.5 pl-10 pr-3.5 py-2 text-left text-[12px] text-[#1C1B1A] hover:bg-[#F0F1EE] transition-colors">
                Stage 2 · Silo art theming
              </button>
            </div>
          )}
        </>
      )}

      {/* Silo Widget — Randy (Oct 2026, P7): the on-platform switch, third in
          the Dark mode / Silo Art / Silo Widget group. It governs whether a
          silo's assistant appears on that silo's own creator pages inside My
          Silo. The website embed is NOT affected — that is each silo's own
          Disable/Revoke. The row only appears once a silo is actually set to
          show on its pages. */}
      {!isFan && data.my_silo_anywhere_available && (
        <button
          type="button"
          onClick={toggleSiloAnywhere}
          role="switch"
          aria-checked={saEnabled}
          data-testid="identity-menu-silo-anywhere"
          className={CTRL_ROW}
        >
          <span className={CTRL_LABEL}>
            <ChatCircleText size={16} weight="duotone" className="shrink-0 text-[#6B6A68]" />
            Silo Widget
          </span>
          <span className={CTRL_SIDE}>
            <PillSwitch on={saEnabled} />
            <span className={CTRL_CARET} aria-hidden="true" />
          </span>
        </button>
      )}

      <div className="border-t border-[#EDEBE6]" />
      <button
        type="button"
        onClick={doLogout}
        data-testid="identity-menu-logout"
        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px] text-[#C16E5A] hover:bg-[#FDF1ED] transition-colors"
      >
        <SignOut size={16} weight="duotone" className="shrink-0" />
        <span>Log out</span>
      </button>
    </div>
  );

  const pickerPanel = siloPicker && (
    <div
      data-testid="silo-art-picker"
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/40"
      onClick={() => setSiloPicker(null)}
    >
      <div className="modal-safe w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#EDEBE6] flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68]">Silo Art · {siloPicker.stage === "stage2" ? "Stage 2" : "Stage 1"}</div>
            <div className="font-serif-display text-lg text-[#1C1B1A] leading-tight">Which silo?</div>
          </div>
          <button onClick={() => setSiloPicker(null)} data-testid="silo-art-picker-close" className="text-[#6B6A68] hover:text-[#1C1B1A] p-1"><X size={16} /></button>
        </div>
        {siloPicker.silos.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <p className="text-sm text-[#6B6A68] mb-4">You don't have any silos yet — create your first one to start dressing its cube art.</p>
            <button onClick={() => { setSiloPicker(null); go("/dashboard"); }} data-testid="silo-art-picker-create"
              className="inline-flex items-center px-4 py-2 rounded-lg bg-[#1C1B1A] text-white text-sm hover:bg-[#3A4A3E] transition-colors">Create your first silo</button>
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto p-3 space-y-1.5">
            {siloPicker.silos.map((s) => (
              <button key={s.id} onClick={() => openStudioFor(s.id, siloPicker.stage)}
                data-testid={`silo-art-picker-silo-${s.id}`}
                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-[#F0F1EE] transition-colors text-left">
                <span className="w-12 h-12 rounded-md overflow-hidden border border-[#E4E1DA] bg-[#EEEDEA] shrink-0 flex items-center justify-center">
                  {faceImageUrl(s, "center")
                    ? <img src={faceImageUrl(s, "center")} alt="" className="w-full h-full object-cover" />
                    : <PaintBrush size={18} weight="duotone" className="text-[#B9B6AF]" />}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-[#1C1B1A] truncate">{s.name}</span>
                  <span className="block text-[11px] text-[#6B6A68] truncate">/{s.slug}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div ref={wrapperRef} className={`relative ${inline ? "inline-flex" : "flex"} items-center`} data-testid="identity-pill-wrapper">
      {/* DESKTOP trigger — the compact identity pill. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="identity-pill"
        aria-expanded={open}
        title="Menu"
        style={{ background: `linear-gradient(90deg, ${accent.from}14 0%, transparent 18%), #FFFFFF` }}
        className="hidden sm:inline-flex flex-col items-center text-center rounded-lg border border-[#D8D6D2] pl-3 pr-3.5 py-1.5 transition-all hover:shadow-sm hover:-translate-y-[1px]"
      >
        <span className="flex items-center gap-1.5 text-[12px] font-medium leading-tight text-[#1C1B1A]">
          <Sparkle size={11} weight="duotone" className="opacity-50 -ml-0.5" style={{ color: accent.from }} />
          <span className="truncate max-w-[120px]">{data.identity_label}</span>
          <span className="opacity-40">/</span>
          <span className="uppercase tracking-[0.06em] text-[10px]">{roleLabel}</span>
        </span>
        <span className="text-[10px] opacity-70 leading-tight mt-0.5 text-[#1C1B1A]">
          {data.invited_by_label ? <>Invited by <strong className="font-semibold" data-testid="identity-pill-desktop-invited-by-name">{data.invited_by_label}</strong></> : <>Invited by —</>}
        </span>
      </button>

      {/* MOBILE trigger — compact star square. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="identity-pill-mobile-icon"
        aria-label={`Menu (${data.identity_label} / ${roleLabel})`}
        aria-expanded={open}
        style={{ borderColor: `${accent.from}40` }}
        className="sm:hidden w-9 h-9 rounded-md border-2 bg-white flex items-center justify-center transition-shadow active:shadow-md"
      >
        <Sparkle size={18} weight="duotone" style={{ color: accent.from }} />
      </button>

      {open && menuPos && createPortal(panel, document.body)}
      {siloPicker && createPortal(pickerPanel, document.body)}
      {usageOpen && siloId && createPortal(
        <ReportModal scope="me" days={7} siloFilter={siloId} onClose={() => setUsageOpen(false)} />,
        document.body,
      )}
    </div>
  );
}
